import { useEffect } from 'react';

interface JsonLdOrganization {
  type: 'Organization';
}

interface JsonLdWebPage {
  type: 'WebPage';
  breadcrumb?: { name: string; url: string }[];
}

interface JsonLdService {
  type: 'Service';
  name: string;
  description: string;
  serviceType?: string;
}

interface JsonLdServiceList {
  type: 'ServiceList';
  services: { name: string; description: string; serviceType: string; url: string }[];
}

interface JsonLdBreadcrumb {
  type: 'BreadcrumbList';
  items: { name: string; url: string }[];
}

interface JsonLdItemList {
  type: 'ItemList';
  items: { name: string; url: string }[];
}

interface JsonLdContactPage {
  type: 'ContactPage';
}

interface JsonLdBlogPosting {
  type: 'BlogPosting';
  headline: string;
  datePublished: string;
  dateModified?: string;
  author: string;
  description: string;
  image?: string;
}

interface JsonLdBlog {
  type: 'Blog';
}

interface JsonLdLocalBusiness {
  type: 'LocalBusiness';
  city: string;
  state: string;
}

interface JsonLdFAQPage {
  type: 'FAQPage';
  faqs: { question: string; answer: string }[];
}

type JsonLdSchema =
  | JsonLdOrganization
  | JsonLdWebPage
  | JsonLdService
  | JsonLdServiceList
  | JsonLdBreadcrumb
  | JsonLdItemList
  | JsonLdContactPage
  | JsonLdBlogPosting
  | JsonLdBlog
  | JsonLdLocalBusiness
  | JsonLdFAQPage;

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
  schema?: JsonLdSchema;
  schemas?: JsonLdSchema[];
}

const SITE_NAME = 'Matchless Digital Hub';
const BASE_URL = 'https://matchlessdigitalhub.com';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

const ORG_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: BASE_URL,
  logo: `${BASE_URL}/logo.png`,
  description: 'SaaS and web app development agency in India building custom CRM, ERP and software solutions for startups and enterprises',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Mumbai',
    addressRegion: 'Maharashtra',
    addressCountry: 'IN',
  },
  areaServed: ['Mumbai', 'Delhi', 'India'],
  serviceType: ['SaaS Development', 'CRM Development', 'ERP Development', 'Web App Development'],
  sameAs: [
    'https://twitter.com/matchlessdigital',
    'https://linkedin.com/company/matchlessdigital',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    availableLanguage: ['English', 'Hindi'],
  },
};

function setMeta(name: string, content: string, property = false) {
  const attr = property ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function buildJsonLd(schema: JsonLdSchema, canonicalUrl: string, title: string, description: string): object {
  switch (schema.type) {
    case 'Organization':
      return ORG_SCHEMA;

    case 'WebPage':
      return {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: canonicalUrl,
        isPartOf: { '@id': BASE_URL },
        ...(schema.breadcrumb && schema.breadcrumb.length > 0 && {
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: schema.breadcrumb.map((crumb, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: crumb.name,
              item: crumb.url,
            })),
          },
        }),
      };

    case 'Service':
      return {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: schema.name,
        description: schema.description,
        ...(schema.serviceType && { serviceType: schema.serviceType }),
        provider: {
          '@type': 'Organization',
          name: SITE_NAME,
          url: BASE_URL,
        },
        areaServed: {
          '@type': 'Country',
          name: 'India',
        },
        url: canonicalUrl,
      };

    case 'ServiceList':
      return {
        '@context': 'https://schema.org',
        '@graph': schema.services.map((svc) => ({
          '@type': 'Service',
          name: svc.name,
          description: svc.description,
          serviceType: svc.serviceType,
          url: `${BASE_URL}${svc.url}`,
          provider: {
            '@type': 'Organization',
            name: SITE_NAME,
            url: BASE_URL,
          },
          areaServed: {
            '@type': 'Country',
            name: 'India',
          },
        })),
      };

    case 'BreadcrumbList':
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: schema.items.map((item, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: item.name,
          item: item.url,
        })),
      };

    case 'ItemList':
      return {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: title,
        description,
        url: canonicalUrl,
        itemListElement: schema.items.map((item, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: item.name,
          url: item.url,
        })),
      };

    case 'ContactPage':
      return {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: title,
        description,
        url: canonicalUrl,
        mainEntity: ORG_SCHEMA,
      };

    case 'BlogPosting':
      return {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: schema.headline,
        description: schema.description,
        datePublished: schema.datePublished,
        dateModified: schema.dateModified ?? schema.datePublished,
        url: canonicalUrl,
        ...(schema.image && {
          image: {
            '@type': 'ImageObject',
            url: schema.image,
          },
        }),
        author: {
          '@type': 'Organization',
          name: schema.author,
          url: BASE_URL,
        },
        publisher: {
          '@type': 'Organization',
          name: SITE_NAME,
          url: BASE_URL,
          logo: {
            '@type': 'ImageObject',
            url: `${BASE_URL}/logo.png`,
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': canonicalUrl,
        },
      };

    case 'Blog':
      return {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: title,
        description,
        url: canonicalUrl,
        publisher: {
          '@type': 'Organization',
          name: SITE_NAME,
          url: BASE_URL,
        },
      };

    case 'LocalBusiness':
      return {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: SITE_NAME,
        url: BASE_URL,
        description,
        address: {
          '@type': 'PostalAddress',
          addressLocality: schema.city,
          addressRegion: schema.state,
          addressCountry: 'IN',
        },
        areaServed: {
          '@type': 'City',
          name: schema.city,
        },
        serviceType: 'Software Development',
      };

    case 'FAQPage':
      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: schema.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      };

    default:
      return {};
  }
}

function injectJsonLd(data: object, index = 0) {
  const id = `jsonld-schema-${index}`;
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function clearExtraJsonLd(count: number) {
  let i = count;
  while (true) {
    const el = document.getElementById(`jsonld-schema-${i}`);
    if (!el) break;
    el.remove();
    i++;
  }
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogImage = DEFAULT_IMAGE,
  ogType = 'website',
  noIndex = false,
  schema,
  schemas,
}: SEOProps) {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    document.title = fullTitle;

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : BASE_URL + window.location.pathname;
    setLink('canonical', canonicalUrl);

    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:image', ogImage, true);
    setMeta('og:site_name', SITE_NAME, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    setMeta('twitter:image', ogImage);
    setMeta('twitter:site', '@matchlessdigital');

    const allSchemas: JsonLdSchema[] = [];
    if (schema) allSchemas.push(schema);
    if (schemas) allSchemas.push(...schemas);

    allSchemas.forEach((s, i) => {
      const jsonLd = buildJsonLd(s, canonicalUrl, fullTitle, description);
      injectJsonLd(jsonLd, i);
    });
    clearExtraJsonLd(allSchemas.length);
  }, [title, description, keywords, canonical, ogImage, ogType, noIndex, schema, schemas]);
}
